import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TouchableOpacity,
  Image,
  Dimensions,
  FlatList,
  NativeSyntheticEvent,
  NativeScrollEvent,
  Platform,
  Pressable,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { SVG_COMPONENTS } from '@/assets/admin';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { useMarketplaceColors } from '@/constants/residents/marketplaceColors';
import { onColor } from '@/constants/shared/theme/onColor';
import { CarouselDots } from './CarouselDots';
import { useResponsive } from '@/hooks/common/useResponsive';

// Icons
const CloseIcon = SVG_COMPONENTS.CANCEL_X;
const ChatIcon = RESIDENTS_SVG_COMPONENTS.CONVERSATION;
const FlagIcon = RESIDENTS_SVG_COMPONENTS.FINISH;
const PropertyPlaceholder = RESIDENTS_SVG_COMPONENTS.PROPERTY_PLACEHOLDER;
const ChevronLeftIcon = SVG_COMPONENTS.CHEVRON_LEFT;
const ChevronRightIcon = SVG_COMPONENTS.CHEVRON_RIGHT;

interface PhotoGalleryModalProps {
  visible: boolean;
  onClose: () => void;
  images: string[];
  initialIndex?: number;
  onContactSeller: () => void;
  onReport: () => void;
}

export const PhotoGalleryModal: React.FC<PhotoGalleryModalProps> = ({
  visible,
  onClose,
  images,
  initialIndex = 0,
  onContactSeller,
  onReport,
}) => {
  const insets = useSafeAreaInsets();
  const mc = useMarketplaceColors();

  const styles = useMemo(() => StyleSheet.create({
    // ============ MOBILE STYLES ============
    container: {
      flex: 1,
      backgroundColor: mc.cardBackground,
    },
  
    // Close Button
    closeButton: {
      position: 'absolute',
      top: 60,
      right: 20,
      width: 20,
      height: 20,
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10,
    },

    // Image Gallery
    imageContainer: {
      flex: 1,
      justifyContent: 'center',
    },
    galleryImage: {
      // width and height set dynamically
    },
    placeholderContainer: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: mc.uploadAreaBackground,
    },

    // Bottom Buttons
    bottomButtons: {
      flexDirection: 'row',
      gap: 16,
      paddingHorizontal: 25,
      paddingTop: 20,
    },
    contactButton: {
      flex: 1,
      height: 34,
      backgroundColor: mc.buttonPrimary,
      borderRadius: 8,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      paddingHorizontal: 16,
    },
    contactButtonText: {
      fontSize: 12,
      fontWeight: '500',
      color: mc.buttonPrimaryText,
      lineHeight: 18,
    },
    reportButton: {
      width: 34,
      height: 34,
      backgroundColor: mc.white5Percent,
      borderRadius: 8,
      alignItems: 'center',
      justifyContent: 'center',
    },

    // ============ DESKTOP/TABLET STYLES ============
    desktopOverlay: {
      flex: 1,
      backgroundColor: 'rgba(0, 0, 0, 0.85)',
      justifyContent: 'center',
      alignItems: 'center',
    },
    desktopContentWrapper: {
      alignItems: 'center',
      justifyContent: 'center',
      padding: 40,
      maxWidth: '95%',
      maxHeight: '95%',
    },
    desktopCloseButton: {
      position: 'absolute',
      top: 0,
      right: 0,
      width: 44,
      height: 44,
      borderRadius: 22,
      backgroundColor: mc.white10Percent,
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10,
    },
    desktopMainContent: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 20,
    },
    desktopImageContainer: {
      borderRadius: 16,
      overflow: 'hidden',
      backgroundColor: mc.cardBackground,
    },
    desktopGalleryImage: {
      borderRadius: 16,
    },
    desktopImageCounter: {
      position: 'absolute',
      top: 16,
      left: 16,
      backgroundColor: mc.priceBadgeBg,
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderRadius: 8,
    },
    desktopImageCounterText: {
      fontSize: 14,
      fontWeight: '500',
      // Sits on the price-badge scrim, which is a fixed dark translucent
      // black in both themes — a themed ink would go near-black in light.
      color: onColor(mc.priceBadgeBg),
    },
    arrowButton: {
      width: 48,
      height: 48,
      borderRadius: 24,
      backgroundColor: mc.white10Percent,
      alignItems: 'center',
      justifyContent: 'center',
    },
    arrowButtonLeft: {
      // Left arrow positioning
    },
    arrowButtonRight: {
      // Right arrow positioning
    },
    arrowButtonDisabled: {
      opacity: 0.4,
    },
    desktopDotsContainer: {
      marginTop: 20,
    },
    desktopDotsContainerOverride: {
      position: 'relative',
      bottom: 0,
    },
    desktopBottomButtons: {
      flexDirection: 'row',
      gap: 16,
      marginTop: 24,
      justifyContent: 'center',
    },
    desktopContactButton: {
      height: 44,
      backgroundColor: mc.buttonPrimary,
      borderRadius: 10,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      paddingHorizontal: 24,
      minWidth: 180,
    },
    desktopContactButtonText: {
      fontSize: 14,
      fontWeight: '600',
      color: mc.buttonPrimaryText,
      lineHeight: 20,
    },
    desktopReportButton: {
      width: 44,
      height: 44,
      backgroundColor: mc.white10Percent,
      borderRadius: 10,
      alignItems: 'center',
      justifyContent: 'center',
    },
}), [mc]);

  const flatListRef = useRef<FlatList>(null);
  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const [mobileContainerHeight, setMobileContainerHeight] = useState(0);
  const { width: screenWidth, height: screenHeight, layoutMode, isWeb } = useResponsive();
  
  const isDesktop = layoutMode === 'desktop';
  const isTablet = layoutMode === 'tablet';
  const isLargeScreen = isDesktop || isTablet;
  
  // Image sizing - desktop uses larger centered image
  const getImageDimensions = () => {
    if (isDesktop) {
      const maxWidth = Math.min(screenWidth * 0.7, 900);
      const maxHeight = screenHeight * 0.75;
      return { width: maxWidth, height: maxHeight };
    }
    if (isTablet) {
      const maxWidth = Math.min(screenWidth * 0.8, 700);
      const maxHeight = screenHeight * 0.65;
      return { width: maxWidth, height: maxHeight };
    }
    return { width: screenWidth, height: screenHeight * 0.6 };
  };
  
  const imageDimensions = getImageDimensions();
  const hasImages = images && images.length > 0;
  const canGoNext = activeIndex < images.length - 1;
  const canGoPrev = activeIndex > 0;

  const handleScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const contentOffset = event.nativeEvent.contentOffset.x;
    const slideWidth = isLargeScreen ? imageDimensions.width : screenWidth;
    const index = Math.round(contentOffset / slideWidth);
    setActiveIndex(index);
  };

  const goToNext = useCallback(() => {
    if (canGoNext) {
      const newIndex = activeIndex + 1;
      setActiveIndex(newIndex);
      flatListRef.current?.scrollToIndex({ index: newIndex, animated: true });
    }
  }, [activeIndex, canGoNext]);

  const goToPrev = useCallback(() => {
    if (canGoPrev) {
      const newIndex = activeIndex - 1;
      setActiveIndex(newIndex);
      flatListRef.current?.scrollToIndex({ index: newIndex, animated: true });
    }
  }, [activeIndex, canGoPrev]);

  // Keyboard navigation for desktop
  useEffect(() => {
    if (!visible || Platform.OS !== 'web') return;
    
    const w: any = (globalThis as any)?.window;
    if (!w) return;

    const handleKeyDown = (e: any) => {
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowRight':
          goToNext();
          break;
        case 'ArrowLeft':
          goToPrev();
          break;
      }
    };

    w.addEventListener('keydown', handleKeyDown);
    return () => w.removeEventListener('keydown', handleKeyDown);
  }, [visible, onClose, goToNext, goToPrev]);

  // Scroll to initial index when modal opens
  useEffect(() => {
    if (visible && flatListRef.current && initialIndex > 0) {
      setTimeout(() => {
        flatListRef.current?.scrollToIndex({ index: initialIndex, animated: false });
      }, 100);
    }
    setActiveIndex(initialIndex);
  }, [visible, initialIndex]);

  // Desktop/Tablet Lightbox Layout
  if (isLargeScreen) {
    return (
      <Modal
        visible={visible}
        animationType="fade"
        transparent={true}
        onRequestClose={onClose}
      >
        {/* Dark overlay - click to close */}
        <Pressable style={styles.desktopOverlay} onPress={onClose}>
          {/* Content container - prevent close on click */}
          <Pressable style={styles.desktopContentWrapper} onPress={(e) => e.stopPropagation()}>
            {/* Close Button - top right */}
            <TouchableOpacity
              style={styles.desktopCloseButton}
              onPress={onClose}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              <CloseIcon width={20} height={20} color={mc.textPrimary} />
            </TouchableOpacity>

            {/* Main content area with arrows */}
            <View style={styles.desktopMainContent}>
              {/* Left Arrow */}
              {images.length > 1 && (
                <TouchableOpacity
                  style={[styles.arrowButton, styles.arrowButtonLeft, !canGoPrev && styles.arrowButtonDisabled]}
                  onPress={goToPrev}
                  disabled={!canGoPrev}
                  activeOpacity={0.7}
                >
                  <ChevronLeftIcon 
                    width={28} 
                    height={28} 
                    color={canGoPrev ? mc.textPrimary : mc.textMuted} 
                  />
                </TouchableOpacity>
              )}

              {/* Image Container */}
              <View style={[styles.desktopImageContainer, { width: imageDimensions.width, height: imageDimensions.height }]}>
                {hasImages ? (
                  <>
                    <FlatList
                      ref={flatListRef}
                      data={images}
                      horizontal
                      pagingEnabled
                      showsHorizontalScrollIndicator={false}
                      onScroll={handleScroll}
                      scrollEventThrottle={16}
                      keyExtractor={(_, index) => index.toString()}
                      getItemLayout={(_, index) => ({
                        length: imageDimensions.width,
                        offset: imageDimensions.width * index,
                        index,
                      })}
                      renderItem={({ item }) => (
                        <Image
                          source={{ uri: item }}
                          style={[styles.desktopGalleryImage, { width: imageDimensions.width, height: imageDimensions.height }]}
                          resizeMode="contain"
                        />
                      )}
                    />

                    {/* Image counter */}
                    <View style={styles.desktopImageCounter}>
                      <Text style={styles.desktopImageCounterText}>
                        {activeIndex + 1} / {images.length}
                      </Text>
                    </View>
                  </>
                ) : (
                  <View style={styles.placeholderContainer}>
                    <PropertyPlaceholder width={imageDimensions.width * 0.4} height={imageDimensions.height * 0.4} />
                  </View>
                )}
              </View>

              {/* Right Arrow */}
              {images.length > 1 && (
                <TouchableOpacity
                  style={[styles.arrowButton, styles.arrowButtonRight, !canGoNext && styles.arrowButtonDisabled]}
                  onPress={goToNext}
                  disabled={!canGoNext}
                  activeOpacity={0.7}
                >
                  <ChevronRightIcon 
                    width={28} 
                    height={28} 
                    color={canGoNext ? mc.textPrimary : mc.textMuted} 
                  />
                </TouchableOpacity>
              )}
            </View>

            {/* Carousel Dots for desktop */}
            {images.length > 1 && (
              <View style={styles.desktopDotsContainer}>
                <CarouselDots 
                  count={images.length} 
                  activeIndex={activeIndex}
                  onDotPress={(index) => {
                    setActiveIndex(index);
                    flatListRef.current?.scrollToIndex({ index, animated: true });
                  }}
                  containerStyle={styles.desktopDotsContainerOverride}
                />
              </View>
            )}

            {/* Bottom Action Buttons */}
            <View style={styles.desktopBottomButtons}>
              <TouchableOpacity 
                style={styles.desktopContactButton}
                onPress={onContactSeller}
                activeOpacity={0.8}
              >
                <ChatIcon width={18} height={18} color={mc.buttonPrimaryText} />
                <Text style={styles.desktopContactButtonText}>Contact seller</Text>
              </TouchableOpacity>
              
              <TouchableOpacity 
                style={styles.desktopReportButton}
                onPress={onReport}
                activeOpacity={0.8}
              >
                <FlagIcon width={20} height={20} color={mc.textPrimary} />
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    );
  }

  // Mobile Layout (original)
  return (
    <Modal
      visible={visible}
      animationType="fade"
      presentationStyle="fullScreen"
      onRequestClose={onClose}
    >
      <View style={[styles.container, { paddingTop: insets.top }]}>
        {/* Close Button */}
        <TouchableOpacity
          style={styles.closeButton}
          onPress={onClose}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
        >
          <CloseIcon width={16} height={16} color={mc.textPrimary} />
        </TouchableOpacity>

        {/* Image Gallery */}
        <View
          style={styles.imageContainer}
          onLayout={(e) => setMobileContainerHeight(e.nativeEvent.layout.height)}
        >
          {hasImages ? (
            <>
              <FlatList
                ref={flatListRef}
                data={images}
                horizontal
                pagingEnabled
                showsHorizontalScrollIndicator={false}
                onScroll={handleScroll}
                scrollEventThrottle={16}
                keyExtractor={(_, index) => index.toString()}
                getItemLayout={(_, index) => ({
                  length: screenWidth,
                  offset: screenWidth * index,
                  index,
                })}
                renderItem={({ item }) => (
                  <Image
                    source={{ uri: item }}
                    style={[styles.galleryImage, { width: screenWidth, height: mobileContainerHeight }]}
                    resizeMode="contain"
                  />
                )}
              />

              {/* Carousel Dots */}
              <CarouselDots 
                count={images.length} 
                activeIndex={activeIndex}
              />
            </>
          ) : (
            <View style={styles.placeholderContainer}>
              <PropertyPlaceholder width={screenWidth * 0.5} height={screenWidth * 0.5} />
            </View>
          )}
        </View>

        {/* Bottom Action Buttons */}
        <View style={[styles.bottomButtons, { paddingBottom: Math.max(insets.bottom, 20) }]}>
          <TouchableOpacity 
            style={styles.contactButton}
            onPress={onContactSeller}
            activeOpacity={0.8}
          >
            <ChatIcon width={18} height={18} color={mc.buttonPrimaryText} />
            <Text style={styles.contactButtonText}>Contact seller</Text>
          </TouchableOpacity>
          
          <TouchableOpacity 
            style={styles.reportButton}
            onPress={onReport}
            activeOpacity={0.8}
          >
            <FlagIcon width={20} height={20} color={mc.textPrimary} />
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
};

